import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Trophy, Users, Award, Settings, Mail, Plus, Trash2, CheckCircle, Clock, XCircle, BarChart3, Star, Send, Save, RefreshCw, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function CandidateCard({ candidate, index, onDelete }) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4 flex items-center gap-4">
        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
          {index + 1}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold">{candidate.name}</h3>
            <Badge variant="outline">{candidate.area}</Badge>
          </div>
          <p className="text-sm text-gray-600 line-clamp-2">{candidate.description}</p>
        </div>
        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => onDelete(candidate.id)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

function EvaluatorCard({ evaluator, onNotify, onDelete }) {
  const getStatusIcon = () => {
    if (evaluator.has_evaluated) return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (evaluator.notification_sent) return <Clock className="h-5 w-5 text-yellow-600" />;
    return <XCircle className="h-5 w-5 text-gray-400" />;
  };
  
  const getStatusClass = () => {
    if (evaluator.has_evaluated) return "bg-green-100";
    if (evaluator.notification_sent) return "bg-yellow-100";
    return "bg-gray-100";
  };
  
  const getStatusBadge = () => {
    if (evaluator.has_evaluated) return <Badge className="bg-green-100 text-green-700">Completado</Badge>;
    if (evaluator.notification_sent) return <Badge className="bg-yellow-100 text-yellow-700">Pendiente</Badge>;
    return <Button size="sm" variant="outline" onClick={() => onNotify([evaluator.id])}><Send className="h-4 w-4" /></Button>;
  };

  return (
    <Card>
      <CardContent className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusClass()}`}>
            {getStatusIcon()}
          </div>
          <div>
            <h4 className="font-medium">{evaluator.name}</h4>
            <p className="text-sm text-gray-500">{evaluator.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge()}
          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => onDelete(evaluator.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ResultCard({ result, index }) {
  const getRankingClass = () => {
    if (index === 0) return "bg-amber-50 border border-amber-200";
    if (index === 1) return "bg-gray-50 border border-gray-200";
    if (index === 2) return "bg-orange-50 border border-orange-200";
    return "bg-white border";
  };
  
  const getBadgeClass = () => {
    if (index === 0) return "bg-amber-500 text-white";
    if (index === 1) return "bg-gray-400 text-white";
    if (index === 2) return "bg-orange-400 text-white";
    return "bg-gray-100";
  };

  return (
    <div className={`flex items-center gap-4 p-3 rounded-lg ${getRankingClass()}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${getBadgeClass()}`}>
        {index + 1}
      </div>
      <div className="flex-1">
        <h4 className="font-semibold">{result.candidate?.name || 'N/A'}</h4>
        <p className="text-sm text-gray-500">{result.candidate?.area || ''}</p>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex">
          <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
          <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
          <Star className="h-4 w-4 text-gray-300" />
        </div>
        <Badge className="bg-emerald-600 text-white">{result.total_score}</Badge>
      </div>
    </div>
  );
}

export default function ShakersAdminPage() {
  const { token, isAdmin } = useAuth();
  const [year, setYear] = useState(2025);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [evaluators, setEvaluators] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [stats, setStats] = useState(null);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState("candidates");
  const [showNewCandidate, setShowNewCandidate] = useState(false);
  const [showNewEvaluator, setShowNewEvaluator] = useState(false);
  const [newCandidate, setNewCandidate] = useState({ name: "", description: "", area: "", country: "España" });
  const [newEvaluator, setNewEvaluator] = useState({ name: "", email: "" });
  const [savingConfig, setSavingConfig] = useState(false);
  const [loadErrors, setLoadErrors] = useState([]);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (token && isAdmin) loadAllData();
  }, [token, year, isAdmin]);

  const loadAllData = async () => {
    setLoading(true);
    setLoadErrors([]);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [r0, r1, r2, r3, r4, r5] = await Promise.all([
        fetch(`${API}/api/shakers/config/${year}`, { headers }),
        fetch(`${API}/api/shakers/candidates?year=${year}`, { headers }),
        fetch(`${API}/api/shakers/evaluators?year=${year}`, { headers }),
        fetch(`${API}/api/shakers/stats/${year}`, { headers }),
        fetch(`${API}/api/shakers/results/${year}`, { headers }),
        fetch(`${API}/api/shakers/evaluations?year=${year}`, { headers }),
      ]);

      const errors = [];
      if (r0.ok) setConfig(await r0.json()); else errors.push(`Config: ${r0.status}`);
      if (r1.ok) setCandidates(await r1.json()); else errors.push(`Candidatos: ${r1.status}`);
      if (r2.ok) setEvaluators(await r2.json()); else errors.push(`Evaluadores: ${r2.status}`);
      if (r3.ok) setStats(await r3.json()); else errors.push(`Stats: ${r3.status} — ${await r3.text()}`);
      if (r4.ok) setResults(await r4.json()); else errors.push(`Resultados: ${r4.status} — ${await r4.text()}`);
      if (r5.ok) setEvaluations(await r5.json()); else errors.push(`Evaluaciones: ${r5.status}`);
      if (errors.length) setLoadErrors(errors);
    } catch (error) {
      toast.error("Error cargando datos");
      setLoadErrors([error.message]);
    } finally {
      setLoading(false);
    }
  };

  const syncStatus = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/shakers/sync-status/${year}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Sincronizado: ${data.evaluations_found} evaluaciones encontradas, ${data.evaluators_updated} evaluadores actualizados`);
        loadAllData();
      } else {
        toast.error(data.detail || "Error al sincronizar");
      }
    } catch (e) {
      toast.error("Error de conexión");
    } finally {
      setSyncing(false);
    }
  };

  const seedDemoData = async () => {
    const res = await fetch(`${API}/api/shakers/seed-demo-data`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    });
    const data = await res.json();
    if (res.ok) { toast.success(data.message); loadAllData(); }
  };

  const createCandidate = async () => {
    const res = await fetch(`${API}/api/shakers/candidates`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ ...newCandidate, year })
    });
    if (res.ok) { toast.success("Candidato creado"); setNewCandidate({ name: "", description: "", area: "", country: "España" }); setShowNewCandidate(false); loadAllData(); }
  };

  const deleteCandidate = async (id) => {
    if (!window.confirm("Eliminar?")) return;
    await fetch(`${API}/api/shakers/candidates/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    toast.success("Eliminado"); loadAllData();
  };

  const createEvaluator = async () => {
    const res = await fetch(`${API}/api/shakers/evaluators`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ ...newEvaluator, year })
    });
    if (res.ok) { toast.success("Evaluador añadido"); setNewEvaluator({ name: "", email: "" }); setShowNewEvaluator(false); loadAllData(); }
  };

  const deleteEvaluator = async (id) => {
    if (!window.confirm("Eliminar?")) return;
    await fetch(`${API}/api/shakers/evaluators/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    toast.success("Eliminado"); loadAllData();
  };

  const notifyEvaluators = async (ids) => {
    const res = await fetch(`${API}/api/shakers/evaluators/notify`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ year, evaluator_ids: ids || [] })
    });
    const data = await res.json();
    toast.success(data.message);
    loadAllData();
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await fetch(`${API}/api/shakers/config/${year}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(config)
      });
      if (res.ok) {
        toast.success("Configuración guardada correctamente");
        loadAllData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Error al guardar");
      }
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSavingConfig(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <Trophy className="h-16 w-16 mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold">Acceso Restringido</h2>
        <p className="text-gray-500">Solo administradores</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const statsData = stats || { total_evaluators: 0, completed_evaluations: 0, completion_rate: 0 };
  const resultsData = results?.results || [];

  return (
    <div className="p-6 space-y-6" data-testid="shakers-admin-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Trophy className="h-8 w-8 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold">Valoriza Shakers {year}</h1>
            <p className="text-gray-500">Premios de Innovación</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="px-3 py-2 border rounded-lg">
            <option value={2025}>2025</option>
            <option value={2026}>2026</option>
            <option value={2027}>2027</option>
          </select>
          {candidates.length === 0 && (
            <Button onClick={seedDemoData} variant="outline"><Plus className="h-4 w-4 mr-2" />Cargar Demo</Button>
          )}
          <Button onClick={syncStatus} variant="outline" disabled={syncing} title="Sincronizar flags de evaluación con los datos reales de la BD">
            {syncing ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Sincronizar
          </Button>
          <Button onClick={loadAllData} variant="outline" size="icon"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Error diagnostic banner */}
      {loadErrors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1">
          <p className="text-sm font-semibold text-red-700">Errores al cargar datos ({loadErrors.length}):</p>
          {loadErrors.map((err, i) => (
            <p key={i} className="text-xs text-red-600 font-mono">{err}</p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-2 bg-amber-100 rounded-lg"><Award className="h-5 w-5 text-amber-600" /></div><div><p className="text-2xl font-bold">{candidates.length}</p><p className="text-sm text-gray-500">Candidatos</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-2 bg-blue-100 rounded-lg"><Users className="h-5 w-5 text-blue-600" /></div><div><p className="text-2xl font-bold">{statsData.total_evaluators}</p><p className="text-sm text-gray-500">Evaluadores</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-2 bg-green-100 rounded-lg"><CheckCircle className="h-5 w-5 text-green-600" /></div><div><p className="text-2xl font-bold">{evaluations.length > 0 ? evaluations.length : statsData.completed_evaluations}</p><p className="text-sm text-gray-500">Completadas</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="p-2 bg-purple-100 rounded-lg"><BarChart3 className="h-5 w-5 text-purple-600" /></div><div><p className="text-2xl font-bold">{statsData.completion_rate}%</p><p className="text-sm text-gray-500">Tasa</p></div></CardContent></Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="candidates"><Award className="h-4 w-4 mr-2" />Candidatos ({candidates.length})</TabsTrigger>
          <TabsTrigger value="evaluators"><Users className="h-4 w-4 mr-2" />Evaluadores ({evaluators.length})</TabsTrigger>
          <TabsTrigger value="evaluations"><CheckCircle className="h-4 w-4 mr-2" />Evaluaciones ({evaluations.length})</TabsTrigger>
          <TabsTrigger value="results"><BarChart3 className="h-4 w-4 mr-2" />Resultados</TabsTrigger>
          <TabsTrigger value="settings"><Settings className="h-4 w-4 mr-2" />Config</TabsTrigger>
        </TabsList>

        <TabsContent value="candidates" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setShowNewCandidate(true)}><Plus className="h-4 w-4 mr-2" />Añadir</Button>
          </div>
          {showNewCandidate && (
            <Card className="border-2 border-dashed border-emerald-600">
              <CardHeader><CardTitle>Nuevo Candidato</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input placeholder="Nombre(s)" value={newCandidate.name} onChange={(e) => setNewCandidate({...newCandidate, name: e.target.value})} />
                  <Input placeholder="Área" value={newCandidate.area} onChange={(e) => setNewCandidate({...newCandidate, area: e.target.value})} />
                </div>
                <Textarea placeholder="Descripción" value={newCandidate.description} onChange={(e) => setNewCandidate({...newCandidate, description: e.target.value})} />
                <div className="flex gap-2">
                  <Button onClick={createCandidate}><Save className="h-4 w-4 mr-2" />Guardar</Button>
                  <Button variant="outline" onClick={() => setShowNewCandidate(false)}>Cancelar</Button>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="space-y-3">
            {candidates.map((c, i) => <CandidateCard key={c.id} candidate={c} index={i} onDelete={deleteCandidate} />)}
          </div>
        </TabsContent>

        <TabsContent value="evaluators" className="space-y-4">
          <div className="flex justify-between">
            <Button onClick={() => notifyEvaluators(null)} disabled={evaluators.length === 0} className="bg-blue-600 hover:bg-blue-700"><Mail className="h-4 w-4 mr-2" />Notificar a Todos</Button>
            <Button onClick={() => setShowNewEvaluator(true)}><Plus className="h-4 w-4 mr-2" />Añadir</Button>
          </div>
          {showNewEvaluator && (
            <Card className="border-2 border-dashed border-emerald-600">
              <CardHeader><CardTitle>Nuevo Evaluador</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input placeholder="Nombre" value={newEvaluator.name} onChange={(e) => setNewEvaluator({...newEvaluator, name: e.target.value})} />
                  <Input placeholder="Email" value={newEvaluator.email} onChange={(e) => setNewEvaluator({...newEvaluator, email: e.target.value})} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={createEvaluator}><Save className="h-4 w-4 mr-2" />Guardar</Button>
                  <Button variant="outline" onClick={() => setShowNewEvaluator(false)}>Cancelar</Button>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-2 gap-4">
            {evaluators.map((e) => <EvaluatorCard key={e.id} evaluator={e} onNotify={notifyEvaluators} onDelete={deleteEvaluator} />)}
          </div>
        </TabsContent>

        <TabsContent value="evaluations" className="space-y-4">
          {evaluations.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Clock className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="font-semibold text-lg">No hay evaluaciones registradas para {year}</h3>
                <p className="text-gray-500 text-sm mt-1">Si los evaluadores ya completaron el proceso, prueba "Sincronizar" para actualizar los flags.</p>
                <Button className="mt-4" onClick={syncStatus} disabled={syncing}>
                  {syncing ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  Sincronizar estado
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      {evaluations.length} evaluaciones recibidas — {year}
                    </CardTitle>
                    <CardDescription>Lista detallada de evaluaciones enviadas</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => {
                    const link = document.createElement("a");
                    link.href = `${API}/api/shakers/export/${year}?token=${token}`;
                    link.download = `Shakers_${year}_Evaluaciones.xlsx`;
                    link.click();
                  }}>
                    <Download className="h-4 w-4 mr-2" />Excel
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {evaluations.map((ev, i) => (
                    <div key={ev.id || i} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-sm">
                          {i + 1}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{ev.evaluator_name || ev.evaluator_email}</p>
                          <p className="text-xs text-muted-foreground">{ev.evaluator_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{ev.candidate_evaluations?.length || 0} candidatos evaluados</span>
                        <span>{ev.submitted_at ? new Date(ev.submitted_at).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="results" className="space-y-4">
          {resultsData.length > 0 ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" />Ranking</CardTitle>
                    <CardDescription>Basado en {results?.total_evaluations || 0} evaluaciones</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => {
                    const link = document.createElement("a");
                    link.href = `${API}/api/shakers/export/${year}?token=${token}`;
                    link.download = `Shakers_${year}_Evaluaciones.xlsx`;
                    link.click();
                  }} data-testid="shakers-export-btn">
                    <Download className="h-4 w-4 mr-2" />Descargar Excel
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {resultsData.map((r, i) => <ResultCard key={i} result={r} index={i} />)}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card><CardContent className="p-8 text-center"><BarChart3 className="h-12 w-12 mx-auto text-gray-400 mb-4" /><h3 className="font-semibold">Sin resultados</h3><p className="text-gray-500">Esperando evaluaciones</p></CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Configuración Shakers {year}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-sm font-medium">Título</label><Input value={config?.title || ""} onChange={(e) => setConfig({...config, title: e.target.value})} /></div>
              <div><label className="text-sm font-medium">Descripción</label><Textarea value={config?.description || ""} onChange={(e) => setConfig({...config, description: e.target.value})} /></div>
              <div><label className="text-sm font-medium">Texto Introductorio</label><Textarea rows={4} value={config?.intro_text || ""} onChange={(e) => setConfig({...config, intro_text: e.target.value})} /></div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={config?.evaluation_open || false} onChange={(e) => setConfig({...config, evaluation_open: e.target.checked})} className="rounded" />
                  <span className="text-sm">Evaluación Abierta</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={config?.is_active || false} onChange={(e) => setConfig({...config, is_active: e.target.checked})} className="rounded" />
                  <span className="text-sm">Edición Activa</span>
                </label>
              </div>
              <Button onClick={saveConfig} disabled={savingConfig}>
                <Save className="h-4 w-4 mr-2" />
                {savingConfig ? "Guardando..." : "Guardar Configuración"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
